import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import {
  User,
  Lock,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Mail,
  Key,
  Fingerprint,
  Copy,
  Check,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
  Download,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Monitor,
  Laptop,
  Tablet
} from 'lucide-react'

// Helper per identificare tipo di passkey
const getPasskeyIcon = (name) => {
  const lowerName = name?.toLowerCase() || ''
  if (lowerName.includes('iphone') || lowerName.includes('ipad') || lowerName.includes('mac')) {
    return <Fingerprint className="h-5 w-5" />
  }
  if (lowerName.includes('android') || lowerName.includes('pixel')) {
    return <Fingerprint className="h-5 w-5" />
  }
  if (lowerName.includes('windows')) {
    return <Monitor className="h-5 w-5" />
  }
  return <Key className="h-5 w-5" />
}

export default function ProfilePage() {
  const { user, checkAuth } = useAuth()
  
  // Password change state
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState(null)
  const [loading2FA, setLoading2FA] = useState(true)
  const [setup2FAStep, setSetup2FAStep] = useState(null) // null | 'totp' | 'email' | 'passkey' | 'backup'
  const [totpSetup, setTotpSetup] = useState(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [copied, setCopied] = useState(false)
  const [error2FA, setError2FA] = useState(null)
  const [processing, setProcessing] = useState(false)
  
  // Passkey state
  const [passkeys, setPasskeys] = useState([])
  const [newPasskeyName, setNewPasskeyName] = useState('')
  const [webAuthnSupported, setWebAuthnSupported] = useState(false)

  // Check WebAuthn support
  useEffect(() => {
    setWebAuthnSupported(
      typeof window !== 'undefined' && 
      window.PublicKeyCredential !== undefined
    )
  }, [])

  // Load 2FA status
  useEffect(() => {
    load2FAStatus()
  }, [])

  const load2FAStatus = async () => {
    try {
      setLoading2FA(true)
      const res = await api.get('/auth/2fa/status')
      const data = res.data?.data || {}
      setTwoFactorStatus(data)
      setPasskeys(data.passkeys || [])
    } catch (err) {
      console.error('Load 2FA status error:', err)
      if (err.response?.status === 404) {
        setTwoFactorStatus({ enabled: false, methods: {} })
      }
    } finally {
      setLoading2FA(false)
    }
  }

  // Password change handlers
  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value })
    setMessage({ type: '', text: '' })
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setMessage({ type: '', text: '' })

    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: 'Le nuove password non coincidono' })
      return
    }

    if (passwords.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'La password deve avere almeno 8 caratteri' })
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      })
      setMessage({ type: 'success', text: 'Password cambiata con successo!' })
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Errore nel cambio password' 
      })
    }
    setLoading(false)
  }

  // 2FA Setup - TOTP
  const startTOTPSetup = async () => {
    try {
      setError2FA(null)
      setProcessing(true)
      setSetup2FAStep('totp')
      const res = await api.post('/auth/2fa/totp/setup')
      setTotpSetup(res.data?.data)
    } catch (err) {
      console.error('TOTP setup error:', err)
      setError2FA(err.response?.data?.message || 'Errore nella configurazione TOTP')
    } finally {
      setProcessing(false)
    }
  }

  const verifyTOTP = async () => {
    try {
      setError2FA(null)
      setProcessing(true)
      const res = await api.post('/auth/2fa/totp/verify', { code: verificationCode })
      
      if (res.data?.data?.backupCodes) {
        setBackupCodes(res.data.data.backupCodes)
        setSetup2FAStep('backup')
      } else {
        await load2FAStatus()
        setSetup2FAStep(null)
        setVerificationCode('')
      }
    } catch (err) {
      console.error('TOTP verify error:', err)
      setError2FA(err.response?.data?.message || 'Codice non valido')
    } finally {
      setProcessing(false)
    }
  }

  // 2FA Setup - Email OTP
  const enableEmailOTP = async () => {
    try {
      setError2FA(null)
      setProcessing(true)
      await api.post('/auth/2fa/email/enable')
      await load2FAStatus()
    } catch (err) {
      console.error('Email OTP enable error:', err)
      setError2FA(err.response?.data?.message || 'Errore nell\'attivazione Email OTP')
    } finally {
      setProcessing(false)
    }
  }

  // 2FA Setup - WebAuthn/Passkey
  const startPasskeySetup = async () => {
    if (!webAuthnSupported) {
      setError2FA('Il tuo browser non supporta le Passkey/WebAuthn')
      return
    }

    try {
      setError2FA(null)
      setProcessing(true)
      
      // 1. Ottieni le opzioni di registrazione dal server
      const optionsRes = await api.post('/auth/2fa/webauthn/register-options')
      const options = optionsRes.data?.data

      // 2. Converti base64 in ArrayBuffer
      options.challenge = base64ToArrayBuffer(options.challenge)
      options.user.id = base64ToArrayBuffer(options.user.id)
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64ToArrayBuffer(cred.id)
        }))
      }

      // 3. Crea la credenziale con l'API del browser
      const credential = await navigator.credentials.create({
        publicKey: options
      })

      // 4. Prepara la risposta per il server
      const credentialResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
          attestationObject: arrayBufferToBase64(credential.response.attestationObject)
        },
        name: newPasskeyName || `Passkey ${new Date().toLocaleDateString('it-IT')}`
      }

      // 5. Verifica la credenziale sul server
      await api.post('/auth/2fa/webauthn/register-verify', credentialResponse)

      // 6. Ricarica stato
      await load2FAStatus()
      setNewPasskeyName('')
      setSetup2FAStep(null)

    } catch (err) {
      console.error('Passkey setup error:', err)
      if (err.name === 'NotAllowedError') {
        setError2FA('Registrazione annullata o timeout')
      } else {
        setError2FA(err.response?.data?.message || err.message || 'Errore nella registrazione della Passkey')
      }
    } finally {
      setProcessing(false)
    }
  }

  // Delete passkey
  const deletePasskey = async (credentialId) => {
    if (!confirm('Sei sicuro di voler rimuovere questa Passkey?')) {
      return
    }

    try {
      setError2FA(null)
      await api.delete(`/auth/2fa/webauthn/credentials/${credentialId}`)
      await load2FAStatus()
    } catch (err) {
      console.error('Delete passkey error:', err)
      setError2FA(err.response?.data?.message || 'Errore nella rimozione')
    }
  }

  // Disable 2FA method
  const disableMethod = async (method) => {
    const methodName = method === 'totp' ? 'Google Authenticator' : 'Email OTP'
    if (!confirm(`Sei sicuro di voler disabilitare ${methodName}?`)) {
      return
    }

    try {
      setError2FA(null)
      setProcessing(true)
      await api.post(`/auth/2fa/${method}/disable`)
      await load2FAStatus()
    } catch (err) {
      console.error('Disable 2FA error:', err)
      setError2FA(err.response?.data?.message || 'Errore nella disabilitazione')
    } finally {
      setProcessing(false)
    }
  }

  // Regenerate backup codes
  const regenerateBackupCodes = async () => {
    if (!confirm('I codici di backup attuali verranno invalidati. Continuare?')) {
      return
    }

    try {
      setError2FA(null)
      setProcessing(true)
      const res = await api.post('/auth/2fa/backup-codes/regenerate')
      setBackupCodes(res.data?.data?.backupCodes || [])
      setSetup2FAStep('backup')
    } catch (err) {
      console.error('Regenerate backup codes error:', err)
      setError2FA(err.response?.data?.message || 'Errore nella rigenerazione')
    } finally {
      setProcessing(false)
    }
  }

  // Copy backup codes
  const copyBackupCodes = () => {
    const text = backupCodes.join('\n')
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Download backup codes
  const downloadBackupCodes = () => {
    const text = `CODICI DI BACKUP - IL DIARIO DI BORDO
=====================================
Generati il: ${new Date().toLocaleString('it-IT')}
Account: ${user?.email}

CONSERVA QUESTI CODICI IN UN LUOGO SICURO!
Ogni codice può essere usato una sola volta.

${backupCodes.join('\n')}

=====================================
Se perdi accesso al tuo dispositivo 2FA,
puoi usare uno di questi codici per accedere.
`
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'backup-codes-ildiariodibordo.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Close setup modal
  const closeSetup = () => {
    setSetup2FAStep(null)
    setTotpSetup(null)
    setVerificationCode('')
    setBackupCodes([])
    setNewPasskeyName('')
    setError2FA(null)
    load2FAStatus()
  }

  // Helper functions for WebAuthn
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <User className="h-7 w-7" />
        Profilo
      </h1>
      
      {/* Info Utente */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Informazioni Account
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Nome</label>
            <p className="font-medium text-gray-900 dark:text-white">{user?.nome} {user?.cognome}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
            <p className="font-medium text-gray-900 dark:text-white">{user?.email}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Username</label>
            <p className="font-medium text-gray-900 dark:text-white">{user?.username}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Ruolo</label>
            <p className="font-medium text-gray-900 dark:text-white capitalize">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                {user?.role}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Autenticazione a Due Fattori */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            Autenticazione a Due Fattori (2FA)
          </h2>
          {loading2FA && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
        </div>

        {loading2FA ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Status Overview */}
            <div className="mb-6 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-3">
                {twoFactorStatus?.enabled ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-green-700 dark:text-green-400">2FA Attivo</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Il tuo account è protetto con autenticazione a due fattori
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                      <ShieldOff className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-medium text-yellow-700 dark:text-yellow-400">2FA Non Attivo</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Attiva l'autenticazione a due fattori per maggiore sicurezza
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {error2FA && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 dark:text-red-300">{error2FA}</span>
              </div>
            )}

            {/* Methods */}
            <div className="space-y-4">
              {/* TOTP - Google Authenticator */}
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">Google Authenticator</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Usa un'app authenticator per generare codici
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-13 sm:ml-0">
                    {twoFactorStatus?.methods?.totp ? (
                      <>
                        <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1 whitespace-nowrap">
                          <CheckCircle className="h-4 w-4" />
                          Attivo
                        </span>
                        <button
                          onClick={() => disableMethod('totp')}
                          disabled={processing}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 whitespace-nowrap"
                        >
                          Disabilita
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={startTOTPSetup}
                        disabled={processing}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 whitespace-nowrap"
                      >
                        Configura
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Email OTP */}
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">Email OTP</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        Ricevi codici via email ({user?.email})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-13 sm:ml-0">
                    {twoFactorStatus?.methods?.emailOtp ? (
                      <>
                        <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1 whitespace-nowrap">
                          <CheckCircle className="h-4 w-4" />
                          Attivo
                        </span>
                        <button
                          onClick={() => disableMethod('email')}
                          disabled={processing}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 whitespace-nowrap"
                        >
                          Disabilita
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={enableEmailOTP}
                        disabled={processing}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 whitespace-nowrap"
                      >
                        Attiva
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* WebAuthn / Passkey */}
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <Fingerprint className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white">Passkey / Biometria</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Face ID, Touch ID, Windows Hello, chiavi di sicurezza
                      </p>
                    </div>
                  </div>
                  {webAuthnSupported && passkeys.length < 10 && (
                    <button
                      onClick={() => setSetup2FAStep('passkey')}
                      disabled={processing}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-2 whitespace-nowrap ml-13 sm:ml-0"
                    >
                      <Plus className="h-4 w-4" />
                      Aggiungi
                    </button>
                  )}
                </div>

                {!webAuthnSupported && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Il tuo browser non supporta le Passkey/WebAuthn
                    </p>
                  </div>
                )}

                {/* Lista passkeys registrate */}
                {passkeys.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {passkeys.map((passkey) => (
                      <div 
                        key={passkey.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg gap-2"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                            {getPasskeyIcon(passkey.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                              {passkey.name || 'Passkey'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Aggiunta il {new Date(passkey.createdAt).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => deletePasskey(passkey.id)}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex-shrink-0"
                          title="Rimuovi passkey"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {passkeys.length === 0 && webAuthnSupported && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Nessuna passkey registrata. Aggiungi una passkey per accedere con Face ID, impronta o chiave di sicurezza.
                  </p>
                )}
              </div>

              {/* Backup Codes */}
              {twoFactorStatus?.enabled && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <Key className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">Codici di Backup</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {twoFactorStatus?.backupCodesRemaining || 0} codici rimanenti
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={regenerateBackupCodes}
                      disabled={processing}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2 disabled:opacity-50 whitespace-nowrap ml-13 sm:ml-0"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Rigenera
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Cambio Password */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5" />
          Cambia Password
        </h2>
        
        {message.text && (
          <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password Attuale
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                value={passwords.currentPassword}
                onChange={handlePasswordChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nuova Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Minimo 8 caratteri, 1 maiuscola, 1 numero, 1 carattere speciale
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Conferma Nuova Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Cambia Password
              </>
            )}
          </button>
        </form>
      </div>

      {/* TOTP Setup Modal */}
      {setup2FAStep === 'totp' && totpSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-600" />
              Configura Google Authenticator
            </h3>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                1. Scansiona il QR code con Google Authenticator o un'app simile
              </p>

              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img 
                  src={totpSetup.qrCode} 
                  alt="QR Code" 
                  className="w-48 h-48"
                />
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Oppure inserisci manualmente:
                </p>
                <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                  {totpSetup.secret}
                </code>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                2. Inserisci il codice a 6 cifre generato dall'app
              </p>

              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                maxLength={6}
              />

              {error2FA && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">{error2FA}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeSetup}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={verifyTOTP}
                disabled={verificationCode.length !== 6 || processing}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                Verifica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Passkey Setup Modal */}
      {setup2FAStep === 'passkey' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-green-600" />
              Aggiungi Passkey
            </h3>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Usa Face ID, Touch ID, Windows Hello o una chiave di sicurezza per accedere più velocemente.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome dispositivo (opzionale)
                </label>
                <input
                  type="text"
                  value={newPasskeyName}
                  onChange={(e) => setNewPasskeyName(e.target.value)}
                  placeholder="Es: MacBook Pro, iPhone 15"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  maxLength={50}
                />
              </div>

              {error2FA && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">{error2FA}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeSetup}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={startPasskeySetup}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Registrazione...
                  </>
                ) : (
                  <>
                    <Fingerprint className="h-4 w-4" />
                    Registra Passkey
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Modal */}
      {setup2FAStep === 'backup' && backupCodes.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Key className="h-5 w-5 text-yellow-600" />
              Codici di Backup
            </h3>

            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Salva questi codici in un luogo sicuro. Ogni codice può essere usato una sola volta 
                  per accedere se perdi il tuo dispositivo 2FA.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg font-mono text-sm">
              {backupCodes.map((code, idx) => (
                <div key={idx} className="text-gray-900 dark:text-white">
                  {code}
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={copyBackupCodes}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center justify-center gap-2"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copiato!' : 'Copia'}
              </button>
              <button
                onClick={downloadBackupCodes}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Scarica
              </button>
            </div>

            <button
              onClick={closeSetup}
              className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
            >
              Ho salvato i codici
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
